import {
    Badge,
    BlockStack,
    Button,
    CalloutCard,
    Card,
    Divider,
    Icon,
    InlineGrid,
    InlineStack,
    Layout,
    Page,
    Spinner,
    Text,
} from "@shopify/polaris";
import {
    ArrowRightIcon,
    ChartVerticalIcon,
    CheckCircleIcon,
    DataTableIcon,
    ImportIcon,
    MagicIcon,
    ProductIcon,
    XIcon,
} from "@shopify/polaris-icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { getApiErrorMessage } from "../../app/api/error";
import { AtdDiagnostics } from "../../app/models/Atd";
import { StorefrontSearchReport, StorefrontSearchReportTopSearch } from "../../app/models/Reports";
import { SetupHealth } from "../../app/models/SetupHealth";
import { useStore } from "../../app/stores/store";
import { getShopifyStoreHandle } from "../../app/utils/shopifyAdmin";
import {
    SearchActivityChart,
} from "../reports/SearchActivitySummary";
import { getLatestDashboardFeatureAnnouncement } from "./dashboardFeatureAnnouncements";
import { getSetupHealthPresentation } from "../setup/setupHealthPresentation";
import vcdbReferenceCalloutImage from "../../assets/dashboard/polaris-vcdb-reference.png";

type DashboardMetricProps = {
    label: string;
    value: string | number;
    tone?: "subdued" | "critical";
    onClick?: () => void;
};

const formatDateTime = (value?: string) => {
    if (!value) return "Not synced yet";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Not synced yet";

    return parsed.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

const formatReportDate = (value?: string) => {
    if (!value) return "";

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
};

const getReportDayCount = (report?: StorefrontSearchReport) => {
    if (!report?.from || !report.to) return 30;

    const from = new Date(`${report.from.slice(0, 10)}T00:00:00`);
    const to = new Date(`${report.to.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 30;

    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
};

type DashboardSearchMetricProps = {
    label: string;
    value: number;
    comparisonValue: number;
    showComparison: boolean;
};

const DashboardSearchMetric = ({
    label,
    value,
    comparisonValue,
    showComparison,
}: DashboardSearchMetricProps) => {
    const percentChange = comparisonValue === 0
        ? value === 0 ? 0 : null
        : Math.round(((value - comparisonValue) / comparisonValue) * 100);
    const direction = percentChange == null || percentChange === 0
        ? ""
        : percentChange > 0 ? "↗" : "↘";
    const changeLabel = percentChange == null
        ? "New"
        : `${Math.abs(percentChange)}%`;
    const accessibleChange = percentChange == null
        ? "New compared with the previous period"
        : percentChange === 0
            ? "No change from the previous period"
            : `${Math.abs(percentChange)} percent ${percentChange > 0 ? "increase" : "decrease"} from the previous period`;

    return (
        <div className="pf-dashboard-search-metric">
            <span className="pf-dashboard-search-metric__label">{label}</span>
            <span className="pf-dashboard-search-metric__value-row">
                <strong>{formatCount(value)}</strong>
                {showComparison && (
                    <span
                        className="pf-dashboard-search-metric__change"
                        aria-label={accessibleChange}
                        title="Compared with the previous period"
                    >
                        {direction} {changeLabel}
                    </span>
                )}
            </span>
        </div>
    );
};

const getSearchLabel = (search: StorefrontSearchReportTopSearch) => (
    search.vehicleLabel?.trim()
    || Object.values(search.fieldValues ?? {}).filter(Boolean).join(" ")
    || "Unknown vehicle"
);

const atdLoadingCardDelayMs = 500;
const showAtdDashboardCard = false;
const storeDisplayNameStorageKey = "partfits.dashboard.storeDisplayNames.v1";
const normalizeStoreDisplayName = (value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed) return "";

    return trimmed.replace(/\.myshopify\.com$/i, "");
};

const getCachedStoreDisplayName = (shopHandle: string) => {
    if (!shopHandle) return "";

    try {
        const stored = window.sessionStorage.getItem(storeDisplayNameStorageKey);
        if (!stored) return "";

        const displayNames = JSON.parse(stored) as Record<string, string>;
        return normalizeStoreDisplayName(displayNames[shopHandle]);
    } catch {
        return "";
    }
};

const cacheStoreDisplayName = (shopHandle: string, displayName: string) => {
    if (!shopHandle || !displayName) return;

    try {
        const stored = window.sessionStorage.getItem(storeDisplayNameStorageKey);
        const displayNames = stored ? JSON.parse(stored) as Record<string, string> : {};
        displayNames[shopHandle] = displayName;
        window.sessionStorage.setItem(storeDisplayNameStorageKey, JSON.stringify(displayNames));
    } catch {
        // A missing cache should never block rendering the dashboard.
    }
};

const DashboardMetric = ({ label, value, tone, onClick }: DashboardMetricProps) => {
    const content = (
        <>
            <Text as="p" variant="bodySm" tone="subdued">
                {label}
            </Text>
            <Text as="p" variant="headingLg" tone={tone}>
                {value}
            </Text>
        </>
    );

    if (onClick) {
        return (
            <button type="button" className="pf-dashboard-metric pf-dashboard-metric--button" onClick={onClick}>
                {content}
            </button>
        );
    }

    return (
        <div className="pf-dashboard-metric">
            {content}
        </div>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { userStore } = useStore();
    const [atdDiagnostics, setAtdDiagnostics] = useState<AtdDiagnostics | undefined>();
    const [atdLoading, setAtdLoading] = useState(true);
    const [showAtdLoadingCard, setShowAtdLoadingCard] = useState(false);
    const [atdError, setAtdError] = useState<string | undefined>();
    const [vcdbReferenceCalloutLoaded, setVcdbReferenceCalloutLoaded] = useState(false);
    const [showVcdbReferenceCallout, setShowVcdbReferenceCallout] = useState(false);
    const [dismissedFeatureAnnouncementId, setDismissedFeatureAnnouncementId] = useState("");
    const [searchReport, setSearchReport] = useState<StorefrontSearchReport>();
    const [searchReportLoading, setSearchReportLoading] = useState(true);
    const [searchReportError, setSearchReportError] = useState<string>();
    const [searchReportReloadKey, setSearchReportReloadKey] = useState(0);
    const [setupHealth, setSetupHealth] = useState<SetupHealth>();
    const [setupHealthLoading, setSetupHealthLoading] = useState(true);
    const [setupHealthError, setSetupHealthError] = useState(false);

    useEffect(() => {
        if (!userStore.user) {
            userStore.getUser();
        }
    }, [userStore, userStore.user]);

    useEffect(() => {
        let cancelled = false;

        const loadDashboardSettings = async () => {
            try {
                const settings = await agent.Settings.dashboard();
                if (!cancelled) {
                    setShowVcdbReferenceCallout(!settings.vcdbReferenceCalloutDismissed);
                    setDismissedFeatureAnnouncementId(settings.dismissedFeatureAnnouncementId || "");
                }
            } catch {
                // Keep the reference-data prompt visible if settings cannot load,
                // but only for the current render; persistence remains DB-backed.
                if (!cancelled) {
                    setShowVcdbReferenceCallout(true);
                }
            } finally {
                if (!cancelled) {
                    setVcdbReferenceCalloutLoaded(true);
                }
            }
        };

        loadDashboardSettings();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadSearchReport = async () => {
            setSearchReportLoading(true);
            setSearchReportError(undefined);

            try {
                const report = await agent.Reports.searchActivity({
                    days: 30,
                    completedDaysOnly: true,
                    limit: 5,
                });
                if (!cancelled) setSearchReport(report);
            } catch (error) {
                const message = await getApiErrorMessage(error, "Unable to load search activity.");
                if (!cancelled) setSearchReportError(message);
            } finally {
                if (!cancelled) setSearchReportLoading(false);
            }
        };

        void loadSearchReport();

        return () => {
            cancelled = true;
        };
    }, [searchReportReloadKey]);

    useEffect(() => {
        let cancelled = false;

        const loadSetupHealth = async () => {
            setSetupHealthLoading(true);
            setSetupHealthError(false);

            try {
                const response = await agent.Settings.setupHealth();
                if (!cancelled) setSetupHealth(response);
            } catch {
                if (!cancelled) setSetupHealthError(true);
            } finally {
                if (!cancelled) setSetupHealthLoading(false);
            }
        };

        void loadSetupHealth();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadAtdDiagnostics = async () => {
            if (!showAtdDashboardCard) {
                setAtdLoading(false);
                return;
            }

            try {
                setAtdLoading(true);
                setAtdError(undefined);
                const diagnostics = await agent.Atd.diagnostics();
                if (!cancelled) setAtdDiagnostics(diagnostics);
            } catch (error: any) {
                if (!cancelled) {
                    setAtdDiagnostics(undefined);
                    setAtdError(error?.message || "Unable to load ATD status.");
                }
            } finally {
                if (!cancelled) setAtdLoading(false);
            }
        };

        loadAtdDiagnostics();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!atdLoading) {
            setShowAtdLoadingCard(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShowAtdLoadingCard(true);
        }, atdLoadingCardDelayMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [atdLoading]);

    const currentYear = new Date().getFullYear();
    const storeHandle = getShopifyStoreHandle(userStore.user?.shopDomain || atdDiagnostics?.shopDomain);
    const explicitStoreDisplayName = normalizeStoreDisplayName(userStore.user?.shopName)
        || normalizeStoreDisplayName(atdDiagnostics?.shopName);
    const domainStoreDisplayName = normalizeStoreDisplayName(userStore.user?.shopDomain)
        || normalizeStoreDisplayName(atdDiagnostics?.shopDomain);
    const storeDisplayName = explicitStoreDisplayName
        || getCachedStoreDisplayName(storeHandle)
        || domainStoreDisplayName
        || storeHandle;

    useEffect(() => {
        cacheStoreDisplayName(storeHandle, explicitStoreDisplayName);
    }, [explicitStoreDisplayName, storeHandle]);

    const atdStatusTone = atdLoading ? "info" : atdDiagnostics?.profileActive ? "success" : atdDiagnostics?.profileConfigured ? "attention" : "critical";
    const atdStatusLabel = atdLoading
        ? "Loading"
        : atdDiagnostics?.profileActive
            ? "Active"
            : atdDiagnostics?.profileConfigured
                ? "Configured, inactive"
                : "Not configured";
    const failedOrders = atdDiagnostics?.failedOrderCount ?? 0;
    const atdLinkedProductsPath = "/products?tab=atd&linkStatus=linked";
    const atdUnlinkedProductsPath = "/products?tab=atd&linkStatus=unlinked";
    const showAtdCard = showAtdDashboardCard && (Boolean(atdDiagnostics?.profileConfigured) || Boolean(atdError) || (atdLoading && showAtdLoadingCard));
    const topSearches = searchReport?.topSearches.slice(0, 5) ?? [];
    const highestTopSearchCount = Math.max(1, ...topSearches.map((search) => search.searchCount));
    const reportDayCount = getReportDayCount(searchReport);
    const averageDailySearches = Math.round((searchReport?.totalSearches ?? 0) / reportDayCount);
    const previousAverageDailySearches = Math.round(
        (searchReport?.previousTotalSearches ?? 0) / reportDayCount
    );
    const searchDataAvailableFrom = searchReport?.dataAvailableFrom?.slice(0, 10);
    const searchComparisonComplete = Boolean(
        searchReport?.comparisonEnabled
        && searchReport.dataAvailableFrom !== undefined
        && searchDataAvailableFrom
        && searchReport.previousFrom.slice(0, 10) >= searchDataAvailableFrom
    );
    const activeFeatureAnnouncement = vcdbReferenceCalloutLoaded
        ? getLatestDashboardFeatureAnnouncement(dismissedFeatureAnnouncementId)
        : undefined;
    const setupHealthPresentation = setupHealth
        ? getSetupHealthPresentation(setupHealth.overallStatus)
        : undefined;
    const setupHealthBadgeTone = setupHealth?.overallStatus === "Ready"
        ? "success"
        : setupHealth?.overallStatus === "ActionRequired"
            ? "attention"
            : "info";
    // This replaces the old stale-inventory count with an action-oriented
    // catalog health signal: products linked in Shopify vs products currently
    // available from ATD's latest catalog sync.
    const linkedAvailableProducts = atdLoading
        ? "-"
        : `${formatCount(atdDiagnostics?.linkedProductCount)} / ${formatCount(atdDiagnostics?.availableProductCount ?? atdDiagnostics?.productCount)}`;
    const dismissVcdbReferenceCallout = async () => {
        setShowVcdbReferenceCallout(false);

        try {
            await agent.Settings.dismissVcdbReferenceCallout();
        } catch {
            // Leave it hidden for the active session; a later page load will
            // show it again if the DB save did not complete.
        }
    };
    const dismissFeatureAnnouncement = async () => {
        if (!activeFeatureAnnouncement) return;

        const announcementId = activeFeatureAnnouncement.id;
        setDismissedFeatureAnnouncementId(announcementId);

        try {
            await agent.Settings.dismissFeatureAnnouncement(announcementId);
        } catch {
            // Keep the announcement hidden for the active session. If the
            // setting did not persist, it can be shown again on a later load.
        }
    };
    return (
        <Page>
            <div className="pf-dashboard">
                <Layout>
                    <Layout.Section>
                        <BlockStack gap="500">
                            <div className="pf-dashboard__intro">
                                <div className="pf-dashboard__welcome">
                                    <Text as="h1" variant="headingLg">
                                        {storeDisplayName ? `Hi, ${storeDisplayName}.` : "Hi."} <span style={{ fontWeight: 400 }}>Welcome to PartFits.</span>
                                    </Text>
                                </div>
                            </div>

                            <Card>
                                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                    <BlockStack gap="100">
                                        <InlineStack gap="200" blockAlign="center" wrap>
                                            <Text as="h2" variant="headingMd">Setup &amp; Health</Text>
                                            {setupHealthPresentation ? (
                                                <Badge tone={setupHealthBadgeTone}>{setupHealthPresentation.badge}</Badge>
                                            ) : null}
                                        </InlineStack>
                                        {setupHealthLoading && !setupHealth ? (
                                            <InlineStack gap="200" blockAlign="center">
                                                <Spinner size="small" accessibilityLabel="Checking PartFits setup" />
                                                <Text as="p" variant="bodySm" tone="subdued">Checking your storefront setup…</Text>
                                            </InlineStack>
                                        ) : setupHealth ? (
                                            <Text as="p" variant="bodySm" tone="subdued">{setupHealth.overallMessage}</Text>
                                        ) : (
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {setupHealthError ? "Setup status is temporarily unavailable." : "Review your data and storefront installation."}
                                            </Text>
                                        )}
                                    </BlockStack>
                                    <Button
                                        variant={setupHealth?.overallStatus === "ActionRequired" ? "primary" : undefined}
                                        onClick={() => navigate("/setup-health")}
                                    >
                                        {setupHealth?.overallStatus === "ActionRequired" ? "Resolve setup" : "Check setup"}
                                    </Button>
                                </InlineStack>
                            </Card>

                            {vcdbReferenceCalloutLoaded && showVcdbReferenceCallout ? (
                                <div className="pf-dashboard__vcdb-callout">
                                    <CalloutCard
                                        title="Optional vehicle reference data is available"
                                        illustration={vcdbReferenceCalloutImage}
                                        primaryAction={{
                                            content: "Review reference data",
                                            onAction: () => navigate("/storefront?section=reference-data"),
                                        }}
                                        onDismiss={dismissVcdbReferenceCallout}
                                    >
                                        <p>
                                            PartFits can add standard vehicle values to your admin selectors, giving you a broader starting point when creating fitments. This is turned off by default, so your dropdowns only show vehicles already saved in your fitment data.
                                        </p>
                                    </CalloutCard>
                                </div>
                            ) : null}

                            {showAtdCard ? (
                                <Card>
                                    <BlockStack gap="400">
                                        <InlineStack align="space-between" blockAlign="center" gap="300">
                                            <InlineStack gap="200" blockAlign="center">
                                                <Text as="h2" variant="headingMd">
                                                    ATD integration
                                                </Text>
                                                <Badge tone={atdStatusTone}>{atdStatusLabel}</Badge>
                                            </InlineStack>
                                            <InlineStack gap="200">
                                                <Button onClick={() => navigate("/integrations/atd/orders")}>View orders</Button>
                                                <Button onClick={() => navigate("/integrations/atd/profile")}>Settings</Button>
                                            </InlineStack>
                                        </InlineStack>

                                        <Text as="p" tone="subdued">
                                            ATD product sync, fitment coverage, and Ship-To-Home fulfillment status.
                                        </Text>

                                        {atdError ? (
                                            <Text as="p" tone="critical">
                                                {atdError}
                                            </Text>
                                        ) : atdLoading ? (
                                            <Text as="p" tone="subdued">
                                                Loading ATD integration status...
                                            </Text>
                                        ) : null}

                                        <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                                            <DashboardMetric
                                                label="Products linked to ATD"
                                                value={linkedAvailableProducts}
                                                onClick={() => navigate(atdLinkedProductsPath)}
                                            />
                                            <DashboardMetric
                                                label="ATD orders in progress"
                                                value={atdLoading ? "-" : atdDiagnostics?.inProgressOrderCount ?? 0}
                                            />
                                            <DashboardMetric
                                                label="ATD orders, last 30 days"
                                                value={atdLoading ? "-" : atdDiagnostics?.ordersLast30Days ?? 0}
                                            />
                                            <DashboardMetric
                                                label="Orders needing attention"
                                                value={atdLoading ? "-" : failedOrders}
                                                tone={failedOrders > 0 ? "critical" : undefined}
                                                onClick={() => navigate("/integrations/atd/orders?status=NeedsAttention")}
                                            />
                                        </InlineGrid>

                                        <div className="pf-atd-card__footer">
                                            <Text as="span" tone="subdued">
                                                Last ATD catalog sync: {formatDateTime(atdDiagnostics?.lastCatalogSyncAt)}
                                            </Text>
                                            {atdDiagnostics?.stragglerProductCount ? (
                                                <Button onClick={() => navigate(atdUnlinkedProductsPath)}>
                                                    {`Review ${formatCount(atdDiagnostics.stragglerProductCount)} unlinked product${atdDiagnostics.stragglerProductCount === 1 ? "" : "s"}`}
                                                </Button>
                                            ) : null}
                                        </div>
                                    </BlockStack>
                                </Card>
                            ) : null}

                            <div className="pf-dashboard-feature-cards">
                                <div className="pf-dashboard-feature-card">
                                    <Card>
                                        <BlockStack gap="300">
                                            <InlineStack gap="200" blockAlign="center">
                                                <span className="pf-dashboard-feature-card__icon">
                                                    <Icon source={CheckCircleIcon} />
                                                </span>
                                                <Text as="h2" variant="headingMd">
                                                    Manage your fitment catalog
                                                </Text>
                                            </InlineStack>

                                            <Text as="p" tone="subdued">
                                                Keep product compatibility and your vehicle library accurate from one place.
                                            </Text>

                                            <div className="pf-dashboard-action-list">
                                                <button
                                                    type="button"
                                                    className="pf-dashboard-action-row"
                                                    onClick={() => navigate("/products")}
                                                >
                                                    <span className="pf-dashboard-action-row__icon">
                                                        <Icon source={ProductIcon} />
                                                    </span>
                                                    <span className="pf-dashboard-action-row__content">
                                                        <strong>Product fitment</strong>
                                                        <span>Review coverage and product compatibility</span>
                                                    </span>
                                                    <span className="pf-dashboard-action-row__arrow" aria-hidden="true">
                                                        <Icon source={ArrowRightIcon} />
                                                    </span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="pf-dashboard-action-row"
                                                    onClick={() => navigate("/vehicles")}
                                                >
                                                    <span className="pf-dashboard-action-row__icon">
                                                        <Icon source={DataTableIcon} />
                                                    </span>
                                                    <span className="pf-dashboard-action-row__content">
                                                        <strong>Vehicle library</strong>
                                                        <span>Add, edit, and organize supported vehicles</span>
                                                    </span>
                                                    <span className="pf-dashboard-action-row__arrow" aria-hidden="true">
                                                        <Icon source={ArrowRightIcon} />
                                                    </span>
                                                </button>
                                            </div>
                                        </BlockStack>
                                    </Card>
                                </div>

                                <div className="pf-dashboard-feature-card">
                                    <Card>
                                        <BlockStack gap="300">
                                            <InlineStack gap="200" blockAlign="center">
                                                <span className="pf-dashboard-feature-card__icon">
                                                    <Icon source={MagicIcon} />
                                                </span>
                                                <Text as="h2" variant="headingMd">
                                                    Improve shopper matching
                                                </Text>
                                            </InlineStack>

                                            <Text as="p" tone="subdued">
                                                Build stronger fitment coverage and help shoppers reach the right products faster.
                                            </Text>

                                            <ul className="pf-dashboard-feature-list">
                                                <li>
                                                    <Icon source={CheckCircleIcon} />
                                                    <span>Import CSV, XLSX, ACES, or product-tag fitment data</span>
                                                </li>
                                                <li>
                                                    <Icon source={CheckCircleIcon} />
                                                    <span>Review products that need better vehicle coverage</span>
                                                </li>
                                                <li>
                                                    <Icon source={CheckCircleIcon} />
                                                    <span>Use shopper search trends to prioritize new fitments</span>
                                                </li>
                                            </ul>

                                            <InlineStack gap="200">
                                                <Button
                                                    variant="primary"
                                                    icon={ImportIcon}
                                                    onClick={() => navigate("/imports")}
                                                >
                                                    Start an import
                                                </Button>
                                            </InlineStack>
                                        </BlockStack>
                                    </Card>
                                </div>
                            </div>

                            {activeFeatureAnnouncement ? (
                                <Card>
                                    <div className="pf-dashboard-feature-announcement">
                                        <span className="pf-dashboard-feature-announcement__icon" aria-hidden="true">
                                            <Icon source={ChartVerticalIcon} />
                                        </span>

                                        <BlockStack gap="150">
                                            <InlineStack gap="200" blockAlign="center" wrap>
                                                <Badge tone="info">New</Badge>
                                                <Text as="h2" variant="headingMd">
                                                    {activeFeatureAnnouncement.title}
                                                </Text>
                                            </InlineStack>
                                            <Text as="p" tone="subdued">
                                                {activeFeatureAnnouncement.description}
                                            </Text>
                                        </BlockStack>

                                        <div className="pf-dashboard-feature-announcement__actions">
                                            <Button
                                                variant="primary"
                                                onClick={() => navigate(activeFeatureAnnouncement.actionPath)}
                                            >
                                                {activeFeatureAnnouncement.actionLabel}
                                            </Button>
                                            <Button
                                                accessibilityLabel="Dismiss feature announcement"
                                                icon={XIcon}
                                                variant="tertiary"
                                                onClick={dismissFeatureAnnouncement}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            ) : null}

                            <Card>
                                <BlockStack gap="300">
                                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                        <BlockStack gap="100">
                                            <Text as="h2" variant="headingMd">
                                                Storefront search activity
                                            </Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Last {reportDayCount} complete days
                                                {searchReport?.to ? ` through ${formatReportDate(searchReport.to)}` : ""}.
                                            </Text>
                                        </BlockStack>
                                        <Button onClick={() => navigate("/reports")}>
                                            View reports
                                        </Button>
                                    </InlineStack>

                                    {searchReportError && !searchReport ? (
                                        <div className="pf-dashboard-report__state">
                                            <BlockStack gap="200" inlineAlign="center">
                                                <Text as="p" tone="critical">
                                                    {searchReportError}
                                                </Text>
                                                <Button
                                                    variant="plain"
                                                    onClick={() => setSearchReportReloadKey((key) => key + 1)}
                                                >
                                                    Try again
                                                </Button>
                                            </BlockStack>
                                        </div>
                                    ) : searchReportLoading && !searchReport ? (
                                        <div className="pf-dashboard-report__state">
                                            <InlineStack gap="200" align="center" blockAlign="center">
                                                <Spinner size="small" accessibilityLabel="Loading storefront search activity" />
                                                <Text as="span" tone="subdued">
                                                    Loading search activity...
                                                </Text>
                                            </InlineStack>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="pf-dashboard-search-metrics">
                                                <DashboardSearchMetric
                                                    label="Total searches"
                                                    value={searchReport?.totalSearches ?? 0}
                                                    comparisonValue={searchReport?.previousTotalSearches ?? 0}
                                                    showComparison={searchComparisonComplete}
                                                />
                                                <DashboardSearchMetric
                                                    label="Unique vehicle searches"
                                                    value={searchReport?.uniqueSearchCount ?? 0}
                                                    comparisonValue={searchReport?.previousUniqueSearchCount ?? 0}
                                                    showComparison={searchComparisonComplete}
                                                />
                                                <DashboardSearchMetric
                                                    label="Average searches/day"
                                                    value={averageDailySearches}
                                                    comparisonValue={previousAverageDailySearches}
                                                    showComparison={searchComparisonComplete}
                                                />
                                            </div>

                                            <Divider />

                                            <div className="pf-dashboard-search-insights">
                                                <section
                                                    className="pf-dashboard-search-panel pf-dashboard-search-panel--chart"
                                                    aria-labelledby="pf-dashboard-daily-volume"
                                                >
                                                    <div className="pf-dashboard-search-panel__header">
                                                        <BlockStack gap="050">
                                                            <Text
                                                                as="h3"
                                                                id="pf-dashboard-daily-volume"
                                                                variant="headingSm"
                                                            >
                                                                Daily search volume
                                                            </Text>
                                                            <Text as="p" variant="bodySm" tone="subdued">
                                                                Most recent 7 complete days
                                                            </Text>
                                                        </BlockStack>
                                                    </div>
                                                    <SearchActivityChart
                                                        report={searchReport}
                                                        days={7}
                                                        compact
                                                        embedded
                                                        variant="line"
                                                    />
                                                </section>

                                                <section
                                                    className="pf-dashboard-search-panel"
                                                    aria-labelledby="pf-dashboard-top-searches"
                                                >
                                                    <div className="pf-dashboard-search-panel__header">
                                                        <BlockStack gap="050">
                                                            <Text
                                                                as="h3"
                                                                id="pf-dashboard-top-searches"
                                                                variant="headingSm"
                                                            >
                                                                Top vehicle searches
                                                            </Text>
                                                            <Text as="p" variant="bodySm" tone="subdued">
                                                                Ranked over the last {reportDayCount} complete days
                                                            </Text>
                                                        </BlockStack>
                                                    </div>

                                                    {topSearches.length === 0 ? (
                                                        <div className="pf-dashboard-search-panel__empty">
                                                            <Text as="p" tone="subdued" alignment="center">
                                                                No vehicle searches were recorded in this range.
                                                            </Text>
                                                        </div>
                                                    ) : (
                                                        <div className="pf-dashboard-top-searches">
                                                            {topSearches.map((search, index) => {
                                                                const vehicleLabel = getSearchLabel(search);
                                                                const relativeSearchCount = Math.max(
                                                                    4,
                                                                    (search.searchCount / highestTopSearchCount) * 100
                                                                );

                                                                return (
                                                                    <div
                                                                        className="pf-dashboard-top-search"
                                                                        key={`${vehicleLabel}-${index}`}
                                                                    >
                                                                        <span className="pf-dashboard-top-search__rank">
                                                                            {index + 1}
                                                                        </span>
                                                                        <div className="pf-dashboard-top-search__details">
                                                                            <div className="pf-dashboard-top-search__summary">
                                                                                <span className="pf-dashboard-top-search__vehicle">
                                                                                    {vehicleLabel}
                                                                                </span>
                                                                                <strong className="pf-dashboard-top-search__count">
                                                                                    {formatCount(search.searchCount)}
                                                                                </strong>
                                                                            </div>
                                                                            <div
                                                                                className="pf-dashboard-top-search__track"
                                                                                aria-hidden="true"
                                                                            >
                                                                                <span
                                                                                    style={{ width: `${relativeSearchCount}%` }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </section>
                                            </div>
                                        </>
                                    )}
                                </BlockStack>
                            </Card>

                        </BlockStack>
                    </Layout.Section>
                </Layout>

                <div className="pf-dashboard__footer">
                    &copy; PartFits {currentYear}
                </div>

            </div>
        </Page>
    );
};

export default observer(Dashboard);
